import { motion } from 'framer-motion';
import { Heart, Gift, Star, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

const Donaciones = () => {
  const [montoSeleccionado, setMontoSeleccionado] = useState<number | null>(null);
  const [montoPersonalizado, setMontoPersonalizado] = useState('');

  const montosPredefinidos = [10, 25, 50, 100];

  const beneficios = [
    'Acceso a contenido exclusivo del blog',
    'Descargas gratuitas de guías astrológicas',
    'Prioridad en consultas personalizadas',
    'Descuentos especiales en cursos',
    'Mención en nuestra página de agradecimientos',
  ];

  const handleDonar = () => {
    const monto = montoSeleccionado || parseInt(montoPersonalizado) || 0;
    if (monto > 0) {
      alert(`Gracias por tu donación de $${monto}! Serás redirigido a la pasarela de pago.`);
    }
  };

  return (
    <div className="py-24 px-4 sm:px-6 lg:px-8 bg-purple-950/30">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Heart className="w-16 h-16 text-purple-400 mx-auto mb-6" fill="currentColor" />
          </motion.div>
          <Badge className="mb-4 bg-purple-500/20 text-purple-200 border-purple-400/30">
            <Gift className="w-4 h-4 mr-2" />
            Apoya Nuestro Trabajo
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Tu Apoyo Hace la Diferencia
          </h2>
          <p className="text-lg text-purple-100/60 max-w-2xl mx-auto">
            Ayúdanos a continuar compartiendo el conocimiento astrológico y creando contenido 
            valioso para nuestra comunidad espiritual.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Donation Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card className="bg-purple-950/60 border-purple-500/30 overflow-hidden">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-purple-100 mb-6 text-center">
                  Selecciona un Monto
                </h3>

                {/* Montos predefinidos */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {montosPredefinidos.map((monto) => (
                    <motion.button
                      key={monto}
                      onClick={() => {
                        setMontoSeleccionado(monto);
                        setMontoPersonalizado('');
                      }}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        montoSeleccionado === monto
                          ? 'border-purple-400 bg-purple-500/20'
                          : 'border-purple-500/30 hover:border-purple-400/50 bg-purple-950/40'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="text-2xl font-bold text-purple-300">${monto}</span>
                      <span className="text-sm text-purple-200/50 block">USD</span>
                    </motion.button>
                  ))}
                </div>

                {/* Monto personalizado */}
                <div className="mb-6">
                  <label className="text-sm text-purple-200/70 mb-2 block">
                    O ingresa un monto personalizado
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400 text-lg">$</span>
                    <input
                      type="number"
                      placeholder="Otro monto"
                      value={montoPersonalizado}
                      onChange={(e) => {
                        setMontoPersonalizado(e.target.value);
                        setMontoSeleccionado(null);
                      }}
                      className="w-full pl-10 pr-4 py-4 rounded-xl bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 focus:outline-none focus:border-purple-400 text-lg"
                    />
                  </div>
                </div>

                {/* Botón de donar */}
                <Button
                  onClick={handleDonar}
                  disabled={!montoSeleccionado && !montoPersonalizado}
                  className="w-full py-6 text-lg bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Heart className="w-5 h-5 mr-2" fill="currentColor" />
                  Donar Ahora
                </Button>

                <p className="text-center text-sm text-purple-200/50 mt-4">
                  Pago seguro procesado con encriptación SSL
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-purple-100 mb-4">
                  Beneficios de tu Donación
                </h3>
                <p className="text-purple-100/60 mb-6">
                  Como agradecimiento por tu generosidad, recibirás acceso exclusivo a:
                </p>
              </div>

              <div className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-purple-950/40 border border-purple-500/20"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="p-2 rounded-full bg-purple-500/20">
                      <CheckCircle className="w-5 h-5 text-purple-300" />
                    </div>
                    <span className="text-purple-100">{beneficio}</span>
                  </motion.div>
                ))}
              </div>

              {/* Testimonial */}
              <motion.div
                className="mt-8 p-6 rounded-xl bg-gradient-to-r from-purple-800/30 to-purple-700/30 border border-purple-500/20"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-purple-100/80 italic mb-4">
                  "Donar a Lumina ha sido una de las mejores decisiones. El contenido exclusivo 
                  y la atención personalizada han enriquecido enormemente mi práctica astrológica."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-300" />
                  </div>
                  <div>
                    <p className="text-purple-100 font-medium">Ana María L.</p>
                    <p className="text-sm text-purple-200/50">Miembro desde 2023</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {[
            { valor: '$15K+', label: 'Recaudado' },
            { valor: '200+', label: 'Donantes' },
            { valor: '50+', label: 'Artículos Gratis' },
            { valor: '10K+', label: 'Vidas Impactadas' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="text-center p-6 rounded-xl bg-purple-950/40 border border-purple-500/20"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-3xl font-bold text-purple-300 mb-1">{stat.valor}</div>
              <div className="text-sm text-purple-200/50">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Donaciones;
